import { usePetitionDetailsPageData } from 'src/app/pages/petitionGamma/details/pageContext';

import { getLegislativeBodies, getPrimaryDms, normalizeDms, sortIndividualDms } from './utils';

export type DecisionMaker = {
	id: string;
	displayName: string;
	slug: string | null;
	title: string | null;
	location: string | null;
	photoUrl: string | null;
	signersText: string;
	constituentCount: string | null | undefined;
	publiclyVisible: boolean;
	response?: string;
};

export type LegislativeBody = {
	title: string;
	location: string | null;
	displayedDMs: readonly DecisionMaker[];
	displayViewMoreButton: boolean;
};

type Result = ModelHookResult<{
	primaryDMs: DecisionMaker[] | [];
	legislativeBodies: LegislativeBody[];
	displayViewAllButton: boolean;
}>;

const DISPLAYED_DM_COUNT = 3;

export function useDecisionMakers(): Result {
	const {
		data: {
			petition: {
				primaryDecisionMakerConnection: { nodes: dms },
				dmResponsesConnection: { nodes: allDmResponses },
			},
		},
	} = usePetitionDetailsPageData();

	const politiciansOnly = [...dms].filter((dm) => {
		return dm.type === 'POLITICIAN';
	});

	const primaryDmsList = getPrimaryDms(politiciansOnly);
	const normalizedPrimaryDMs = normalizeDms(primaryDmsList, [...allDmResponses]);
	const sortedPrimaryDMs = sortIndividualDms(normalizedPrimaryDMs);

	const legislativeBodiesMap = getLegislativeBodies(politiciansOnly);
	const legislativeBodies = legislativeBodiesMap.map((body) => {
		const normalizedDms = normalizeDms(body.groupDms, [...allDmResponses]);
		const sortedLBDMs = sortIndividualDms(normalizedDms);

		const hasMissing = sortedLBDMs.some((dm) => !dm.location);
		const uniqueLocations = [...new Set(sortedLBDMs.map((dm) => dm.location))];

		const location = !hasMissing && uniqueLocations.length === 1 ? uniqueLocations[0] : null;

		return {
			title: body.groupTitle,
			location,
			decisionMakers: sortedLBDMs,
		};
	});

	return {
		data: {
			primaryDMs: sortedPrimaryDMs,
			legislativeBodies: legislativeBodies.map(({ decisionMakers, ...rest }) => ({
				...rest,
				displayedDMs: decisionMakers.slice(0, DISPLAYED_DM_COUNT),
				displayViewMoreButton: decisionMakers.length > DISPLAYED_DM_COUNT,
			})),
			displayViewAllButton: true,
		},
	};
}
